'use client'

import {
    CELConstant,
    CELFunction,
    CELType,
    CELVariable,
    EditorServiceGetCELSchemaRequest,
    EditorValidationEnv,
    FormFieldCELContext,
    WorkflowCELContext,
} from 'pbts/c1/api/editor/v1/editor'
import { createContext, FC, ReactNode, useContext, useMemo } from 'react'
import { editorSchemaService } from 'redux_toolkit/editor'

export interface CELSchema {
    variables: CELVariable[]
    functions: CELFunction[]
    types: CELType[]
    constants: CELConstant[]
    isLoading: boolean
    error?: Error
}

interface CELEnvContextValue {
    schema: CELSchema
    validationEnv: EditorValidationEnv
    workflowContext?: WorkflowCELContext
    formFieldContext?: FormFieldCELContext
}

const CELEnvContext = createContext<CELEnvContextValue | null>(null)

export interface CELEnvProviderProps {
    children: ReactNode
    validationEnv: EditorValidationEnv
    workflowContext?: WorkflowCELContext
    formFieldContext?: FormFieldCELContext
}

/**
 * CELEnvProvider fetches and provides CEL schema data for a specific validation environment.
 * This enables the variable browser and other editor features to show context-appropriate
 * variables, functions, and types.
 */
export const CELEnvProvider: FC<CELEnvProviderProps> = ({
    children,
    validationEnv,
    workflowContext,
    formFieldContext,
}) => {
    const request = useMemo((): EditorServiceGetCELSchemaRequest => {
        return EditorServiceGetCELSchemaRequest.create({
            validationEnv,
            workflowContext,
            formFieldContext,
        })
    }, [validationEnv, workflowContext, formFieldContext])

    const { data, isLoading, error } = editorSchemaService.useGetCELSchema(request, {
        actionable: true,
    })

    const schema = useMemo((): CELSchema => ({
        variables: data?.variables ?? [],
        functions: data?.functions ?? [],
        types: data?.types ?? [],
        constants: data?.constants ?? [],
        isLoading,
        error: error as Error | undefined,
    }), [data, isLoading, error])

    const value = useMemo((): CELEnvContextValue => ({
        schema,
        validationEnv,
        workflowContext,
        formFieldContext,
    }), [schema, validationEnv, workflowContext, formFieldContext])

    return <CELEnvContext.Provider value={value}>
        {children}
    </CELEnvContext.Provider>
}

/**
 * useCELEnv provides access to the CEL environment context including schema data.
 * Must be used within a CELEnvProvider.
 */
export function useCELEnv(): CELEnvContextValue {
    const context = useContext(CELEnvContext)
    if (!context) {
        throw new Error('useCELEnv must be used within a CELEnvProvider')
    }
    return context
}

/**
 * useCELSchema is a direct hook for fetching CEL schema data without requiring a provider.
 * Useful for standalone components that need schema information.
 */
export function useCELSchema(
    validationEnv: EditorValidationEnv,
    workflowContext?: WorkflowCELContext,
    formFieldContext?: FormFieldCELContext,
): CELSchema {
    const request = useMemo((): EditorServiceGetCELSchemaRequest => {
        return EditorServiceGetCELSchemaRequest.create({
            validationEnv,
            workflowContext,
            formFieldContext,
        })
    }, [validationEnv, workflowContext, formFieldContext])

    // Skip fetch if validationEnv is UNSPECIFIED (used to disable fetching)
    const shouldFetch = validationEnv !== EditorValidationEnv.UNSPECIFIED

    const { data, isLoading, error } = editorSchemaService.useGetCELSchema(request, {
        actionable: shouldFetch,
    })

    return useMemo((): CELSchema => ({
        variables: data?.variables ?? [],
        functions: data?.functions ?? [],
        types: data?.types ?? [],
        constants: data?.constants ?? [],
        isLoading,
        error: error as Error | undefined,
    }), [data, isLoading, error])
}
